(function () {
  const STORAGE_PREFIX = 'promo-signup-popup:';

  class PromoSignupPopup extends HTMLElement {
    connectedCallback() {
      this._storageId = this.getAttribute('storage-id') || 'default';
      this._overlay = this.querySelector('[data-overlay]');
      this._closeButtons = this.querySelectorAll('[data-close]');
      this._tab = document.querySelector(`[data-promo-tab][data-storage-id="${this._storageId}"]`);
      this._form = this.querySelector('[data-form-panel]');
      this._delay = (parseInt(this.getAttribute('apparition-delay'), 10) || 0) * 1000;
      this._subscribed = this.hasAttribute('data-subscribed');
      this._errorFallback = this.getAttribute('data-error-fallback') || 'Something went wrong. Please try again.';

      this._closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));
      this._overlay?.addEventListener('click', () => this.close());
      this._tab?.addEventListener('click', () => this.open());
      this._form?.addEventListener('submit', (event) => this._onSubmit(event));

      document.addEventListener('keydown', this._onKeydown);

      if (this._getStorage('filled') === 'true' || this._subscribed) {
        this._showSuccessState();
      }

      if (this._shouldAutoOpen()) {
        setTimeout(() => this.open(), this._delay);
      } else {
        this._showTab();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
    }

    _onKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    };

    _key(name) {
      return `${STORAGE_PREFIX}${this._storageId}:${name}`;
    }

    _getStorage(name) {
      try {
        return localStorage.getItem(this._key(name));
      } catch {
        return null;
      }
    }

    _setStorage(name, value) {
      try {
        localStorage.setItem(this._key(name), value);
      } catch {
        /* ignore */
      }
    }

    _shouldAutoOpen() {
      if (this._getStorage('filled') === 'true' || this._subscribed) {
        return false;
      }
      return this._getStorage('closed') !== 'true';
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('lock');
      this._hideTab();
      const input = this.querySelector('.promo-signup-popup__input');
      if (input && !input.closest('[hidden]')) {
        requestAnimationFrame(() => input.focus());
      }
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('lock');

      if (this._getStorage('filled') !== 'true' && !this._subscribed) {
        this._setStorage('closed', 'true');
      }

      this._showTab();
    }

    _showTab() {
      if (this._tab) {
        this._tab.hidden = false;
      }
    }

    _hideTab() {
      if (this._tab) {
        this._tab.hidden = true;
      }
    }

    _showSuccessState() {
      const form = this.querySelector('[data-form-panel]');
      const success = this.querySelector('[data-success-panel]');
      const error = form?.querySelector('[data-form-error]');
      if (form) form.hidden = true;
      if (success) success.hidden = false;
      if (error) error.hidden = true;
    }

    _parseErrorFromHtml(html, formId) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const returnedForm = formId ? doc.getElementById(formId) : null;

      const selectors = [
        '[data-form-error]',
        '.promo-signup-popup__error',
        '.banner--error',
        '.errors li',
        '.form__message--error'
      ];

      for (const selector of selectors) {
        const node = returnedForm?.querySelector(selector) || doc.querySelector(selector);
        if (node?.textContent?.trim()) {
          return node.textContent.trim();
        }
      }

      return null;
    }

    _responseHasError(html, formId) {
      if (!formId) {
        return false;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const returnedForm = doc.getElementById(formId);

      if (!returnedForm) {
        return false;
      }

      return Boolean(returnedForm.querySelector('.errors, .banner--error, [data-form-error]:not([hidden]), .promo-signup-popup__error'));
    }

    async _onSubmit(event) {
      event.preventDefault();

      const form = event.target;
      const submitButton = form.querySelector('[data-submit-button]');
      const errorEl = form.querySelector('[data-form-error]');
      const originalLabel = submitButton?.textContent;

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('is-loading');
      }

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const html = await response.text();

        if (this._responseHasError(html, form.id)) {
          const message = this._parseErrorFromHtml(html, form.id) || this._errorFallback;
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          }
          return;
        }

        this._subscribed = true;
        this._setStorage('filled', 'true');
        this._showSuccessState();
        this.open();
      } catch {
        if (errorEl) {
          errorEl.textContent = this._errorFallback;
          errorEl.hidden = false;
        }
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('is-loading');
          if (originalLabel) {
            submitButton.textContent = originalLabel;
          }
        }
      }
    }
  }

  if (!window.customElements.get('promo-signup-popup')) {
    window.customElements.define('promo-signup-popup', PromoSignupPopup);
  }
})();
